import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { createElement, FC, MouseEventHandler } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import ProjectOrderTarget from '../../../../entity/ProjectOrderTarget';
import { ProjectForAllOfOrganizationFragment } from '../../../../generated/graphql';
import { isDemo } from '../../../../graphql/project';
import theme from '../../../../theme';
import RelativeTime from '../../../connected/RelativeTime';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { useClearRecoilState } from '../../../page/ProjectEditingPage/hooks/useClearRecoilState';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import AnchorTypography from '../../primitive/AnchorTypography';
import _CompetitionBadge from '../../primitive/CompetitionBadge';
import _DemoBadge from '../../primitive/DemoBadge';
import ModificationRequestStateIcon from '../../primitive/ModificationRequestStateIcon';
import _ProjectStateBadge from '../../primitive/ProjectStateBadge';
import AvatarAndName from '../AvatarAndName';
import _Actions from './Actions';
import ProgressSummary from './ProgressSummary';

export type Props = {
  project: ProjectForAllOfOrganizationFragment;
  onClickDeleteButton: MouseEventHandler<HTMLElement>;
  hasDraftProject?: boolean;
  className?: string;
  projectOrderTarget: ProjectOrderTarget;
};

const ProjectListItem: FC<Props> = ({
  project,
  onClickDeleteButton,
  hasDraftProject,
  className,
  projectOrderTarget,
}) => {
  const clearRecoilState = useClearRecoilState();

  const linkUrl = () => {
    if (project.state === 'draft') {
      return `/projects/${project.id}/edit`;
    } else {
      return project.matchingFormat == 'competition'
        ? `/projects/${project.id}/proposal`
        : `/projects/${project.id}/deliverables`;
    }
  };

  return (
    <Root className={className}>
      <AnchorLink href={linkUrl()} onClick={clearRecoilState}>
        <StateValue>
          {project.state && (
            <BadgeWrapper>
              {hasDraftProject ? <ProjectStateBadge projectState={'editing'} /> : null}
              <ProjectStateBadge projectState={project.finalized ? 'finalized' : project.state} />
            </BadgeWrapper>
          )}
        </StateValue>
        <Title variant="subtitle2">
          {project.title} {isDemo(project) ? <DemoBadge>DEMO</DemoBadge> : null}{' '}
          {project.matchingFormat == 'competition' ? (
            <CompetitionBadge>
              <FormattedMessage id="project.badge.competition" />
            </CompetitionBadge>
          ) : null}
        </Title>
        <Descriptions>
          {project.createdBy ? (
            <CreatedBy>
              <AvatarAndName user={project.createdBy} size={16} variant="caption" />
            </CreatedBy>
          ) : null}

          {project[projectOrderTarget]?.isValid ? (
            <DatetimeLabel>
              <FormattedMessage id={`project.${camelToSnake(projectOrderTarget)}`} />
              {' : '}
              <RelativeTime datetime={project[projectOrderTarget] as DateTime} />
            </DatetimeLabel>
          ) : null}
          {project.deliveryDueDate?.isValid &&
          !project.finalized &&
          (project.state === 'submitted' || project.state === 'assigned') &&
          !isDemo(project) ? (
            <DatetimeLabel>
              <FormattedDate value={project.deliveryDueDate.toISO()!} year="numeric" month="numeric" day="numeric">
                {(date: string) => <FormattedMessage id="project.delivery_due_date" values={{ date }} />}
              </FormattedDate>
            </DatetimeLabel>
          ) : null}
          {project.progressSummary && project.progressSummary.modificationRequestFeedbackCount > 0 ? (
            <ModificationRequestStateIconWrap>
              <ModificationRequestStateIcon state="given_feedback" />
            </ModificationRequestStateIconWrap>
          ) : null}
        </Descriptions>
        {project.state !== 'draft' ? (
          <ProgressSummaries>
            <ProgressSummary summary={project.progressSummary} />
          </ProgressSummaries>
        ) : null}
        {project.state === 'draft' ? <Actions project={project} onClickDeleteButton={onClickDeleteButton} /> : null}
      </AnchorLink>
    </Root>
  );
};

const Root = styled('div')({
  borderBottom: `1px solid ${theme.palette.grey['400']}`,
  '&:last-child': {
    borderBottom: 'none',
  },
});

const BadgeWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const ProjectStateBadge = styled(_ProjectStateBadge)({
  marginBottom: '4',
});

const AnchorLink = styled(AnchorTypography)({
  ...gridLayoutContainer({
    columns: ['auto', '16px', '1fr', 'auto', 'auto'],
    rows: ['auto', 'auto'],
    areas: [
      ['state', '.', 'title', 'progress_summary', 'actions'],
      ['state', '.', 'descriptions', 'progress_summary', 'actions'],
    ],
  }),
  [mq('sp')]: {
    ...gridLayoutContainer({
      columns: ['1fr', 'auto'],
      rows: ['auto', `${SPACING_UNIT}px`, 'auto', 'auto', 'auto'],
      areas: [
        ['state', 'state'],
        ['.', '.'],
        ['title', 'title'],
        ['descriptions', 'descriptions'],
        ['progress_summary', 'actions'],
      ],
    }),
  },
  padding: SPACING_REGULAR,
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
    backgroundColor: '#F9FAFD',
  },
});

const StateValue = styled('div')(gridLayoutItem('state', { alignSelf: 'center' }));

const Title = styled(Typography)({
  ...gridLayoutItem('title'),
  marginBottom: SPACING_UNIT,
  wordWrap: 'break-word',
  lineHeight: 1.2,
  fontWeight: 700,
});

const DemoBadge = styled(_DemoBadge)({
  verticalAlign: 'text-bottom',
  marginLeft: 4,
});

const CompetitionBadge = styled(_CompetitionBadge)({
  verticalAlign: 'text-bottom',
  marginLeft: 4,
});

const Descriptions = styled('div')(gridLayoutItem('descriptions'), {
  display: 'flex',
  alignItems: 'center',
  [mq('sp')]: {
    display: 'block',
    marginBottom: SPACING_UNIT / 2,
  },
});

const CreatedBy = styled('div')({
  marginRight: SPACING_REGULAR,
  [mq('sp')]: {
    marginRight: 0,
  },
});

const DatetimeLabel = styled(Typography)({
  marginRight: SPACING_REGULAR,
  whiteSpace: 'nowrap',
  [mq('sp')]: {
    marginRight: SPACING_REGULAR,
    marginTop: 4,
    display: 'inline-block',
  },
});
DatetimeLabel.defaultProps = { variant: 'caption' };

const ModificationRequestStateIconWrap = styled('span')({
  [mq('sp')]: {
    verticalAlign: -5,
    marginLeft: 8,
    display: 'inline-block',
  },
});

const ProgressSummaries = styled('div')({
  ...gridLayoutItem('progress_summary', { justifySelf: 'flex-end', alignSelf: 'center' }),
  [mq('sp')]: {
    ...gridLayoutItem('progress_summary', { justifySelf: 'center', alignSelf: 'center' }),
  },
  display: 'flex',
});

export const Actions = styled(_Actions)({
  ...gridLayoutItem('actions', { justifySelf: 'flex-end', alignSelf: 'center' }),
  marginTop: -SPACING_UNIT,
  marginBottom: -SPACING_UNIT,
});

function camelToSnake(str: string) {
  return str
    .split(/(?=[A-Z])/)
    .join('_')
    .toLowerCase();
}

export default ProjectListItem;
